import React, { useMemo } from "react";
import { AbsoluteFill, Img, OffthreadVideo, staticFile } from "remotion";
import { Animated, Scale, Fade, Move, Rotate } from "remotion-animated";

interface ImageShowStyle {
  entrance: "bounce" | "zoomIn" | "rotateBounce" | "floatIn" | "flipIn";
  theme?: string;
  backgroundColor?: string;
  borderRadius?: string;
  titleColor?: string;
  subtitleColor?: string;
  overlayOpacity?: number;
  objectFit?: "cover" | "contain" | "fill";
  /** 画面占比（占屏幕宽度的百分比 0-1，如 0.8 = 80%），默认 0.85，配合 contain 使用时不完全铺满 */
  scalePercent?: number;
  /** 动画参数（按需覆写默认值） */
  scaleInitial?: number;
  scaleBy?: number;
  scaleMass?: number;
  scaleDamping?: number;
  scaleStiffness?: number;
  fadeInitial?: number;
  rotateDegrees?: number;
  rotateInitial?: number;
  fadeTo?: number;
  moveY?: number;
  animDuration?: number;
}

interface ImageShowProps {
  src: string;
  title?: string;
  subtitle?: string;
  style: ImageShowStyle;
  durationInFrames: number;
  muted?: boolean;
}

/**
 * 俏皮图片入场组件
 *
 * 使用 remotion-animated 的 <Animated> 组件实现多种入场效果。
 * <Animated> 包裹 <Img>，用 absolute 定位撑满。
 */
const ImageShow: React.FC<ImageShowProps> = ({ src, title, subtitle, style: s, durationInFrames }) => {
  // 构建动画数组
  const animations = useMemo(() => {
    const anims: any[] = [];

    switch (s.entrance) {
      case "bounce":
        anims.push(
          Scale({
            by: s.scaleBy ?? 1,
            initial: s.scaleInitial ?? 0,
            mass: s.scaleMass ?? 0.4,
            damping: s.scaleDamping ?? 7,
            stiffness: s.scaleStiffness ?? 100,
          }),
          Fade({ to: s.fadeTo ?? 1, initial: s.fadeInitial ?? 0 }),
        );
        break;

      case "zoomIn":
        // 从大→小：初始 scale=2，弹簧缩到 1
        anims.push(
          Scale({
            by: 1,
            initial: s.scaleInitial ?? 2,
            mass: s.scaleMass ?? 0.5,
            damping: s.scaleDamping ?? 10,
            stiffness: s.scaleStiffness ?? 120,
          }),
          Fade({ to: s.fadeTo ?? 1, initial: s.fadeInitial ?? 0 }),
        );
        break;

      case "rotateBounce":
        anims.push(
          Scale({
            by: 1,
            initial: s.scaleInitial ?? 0.3,
            mass: s.scaleMass ?? 0.5,
            damping: s.scaleDamping ?? 8,
            stiffness: s.scaleStiffness ?? 120,
          }),
          Rotate({
            degrees: s.rotateDegrees ?? 0,
            initial: s.rotateInitial ?? -30,
            mass: 0.5,
            damping: 8,
            stiffness: 120,
          }),
          Fade({ to: s.fadeTo ?? 1, initial: s.fadeInitial ?? 0 }),
        );
        break;

      case "floatIn":
        anims.push(
          Move({ y: 0, initialY: s.moveY ?? 80, mass: 0.6, damping: 18, stiffness: 60 }),
          Fade({ to: s.fadeTo ?? 1, initial: s.fadeInitial ?? 0 }),
        );
        break;

      case "flipIn":
        // 3D 翻牌：绕 Y 轴旋转 -90° → 0°，同时从 0.8 缩放到 1
        anims.push(
          Rotate({
            degreesY: 0,
            initialY: s.rotateInitial ?? -90,
            mass: 0.6,
            damping: 10,
            stiffness: 150,
          }),
          Scale({
            by: 1,
            initial: s.scaleInitial ?? 0.8,
            mass: 0.6,
            damping: 10,
            stiffness: 150,
          }),
          Fade({ to: s.fadeTo ?? 1, initial: s.fadeInitial ?? 0 }),
        );
        break;
    }

    return anims;
  }, [s]);

  // 文本延迟出现的帧数
  const textStart = Math.min(15, Math.floor(durationInFrames * 0.25));

  return (
    <AbsoluteFill
      style={{
        backgroundColor: s.backgroundColor ?? "#0a0a0a",
        overflow: "hidden",
      }}
    >
      {/* 图片用 scalePercent 控制画面占比，居中对齐 */}
      <AbsoluteFill
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: `${(s.scalePercent ?? 0.85) * 100}%`,
            height: `${(s.scalePercent ?? 0.85) * 100}%`,
            position: "relative",
          }}
        >
          <Animated
            animations={animations}
            absolute
            in={0}
            out={durationInFrames}
            style={{
              width: "100%",
              height: "100%",
            }}
          >
            {src.endsWith('.mp4') || src.endsWith('.mov') || src.endsWith('.webm') ? (
              <OffthreadVideo
                src={staticFile(src)}
                volume={0}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: s.objectFit ?? "cover",
                  borderRadius: s.borderRadius ?? 0,
                }}
              />
            ) : (
              <Img
                src={staticFile(src)}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: s.objectFit ?? "cover",
                  borderRadius: s.borderRadius ?? 0,
                }}
              />
            )}
          </Animated>
        </div>
      </AbsoluteFill>

      {/* 遮罩层 */}
      <AbsoluteFill
        style={{
          background:
            `linear-gradient(to top, rgba(0,0,0,${s.overlayOpacity ?? 0.6}) 0%, transparent 50%)`,
        }}
      />

      {/* 标题文字 */}
      {title && (
        <AbsoluteFill
          style={{
            justifyContent: "flex-end",
            alignItems: "flex-start",
            padding: "60px 40px",
          }}
        >
          <Animated
            animations={[
              Fade({ to: 1, initial: 0, start: textStart }),
              Move({ y: 0, initialY: 20, start: textStart, mass: 0.5, damping: 10 }),
            ]}
          >
            <div>
              <h1
                style={{
                  fontSize: 52,
                  fontWeight: 700,
                  color: s.titleColor ?? "#ffffff",
                  margin: 0,
                  lineHeight: 1.2,
                  textShadow: "0 2px 20px rgba(0,0,0,0.5)",
                }}
              >
                {title}
              </h1>
              {subtitle && (
                <p
                  style={{
                    fontSize: 24,
                    fontWeight: 400,
                    color: s.subtitleColor ?? "rgba(255,255,255,0.8)",
                    margin: "12px 0 0 0",
                    textShadow: "0 2px 10px rgba(0,0,0,0.4)",
                  }}
                >
                  {subtitle}
                </p>
              )}
            </div>
          </Animated>
        </AbsoluteFill>
      )}
    </AbsoluteFill>
  );
};

export default ImageShow;
;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
